import React, { useEffect, useRef } from "react";
import { FaCheck, FaSave, FaTimes } from "react-icons/fa";

function CheckboxField({ name, label, checked, onChange }) {
  return (
    <label className="flex min-h-12 cursor-pointer items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 dark:border-white/10 dark:bg-slate-950">
      <input
        type="checkbox"
        name={name}
        checked={Boolean(checked)}
        onChange={onChange}
        className="h-5 w-5 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
      />

      <span className="text-sm font-bold text-slate-800 dark:text-white">
        {label}
      </span>
    </label>
  );
}

function DanGradeModal({
  isOpen,
  form,
  saving,
  onClose,
  onChange,
  onSubmit,
}) {
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    closeButtonRef.current?.focus();

    const handleKeyDown = (keyboardEvent) => {
      if (keyboardEvent.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[90] flex items-end justify-center bg-slate-950/70 px-3 backdrop-blur-sm sm:items-center sm:px-4">
      <div className="relative flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-[2rem] border border-white/20 bg-white shadow-2xl dark:bg-slate-900 sm:rounded-[2rem]">
        <div className="border-b border-slate-200 bg-gradient-to-br from-slate-950 via-orange-950 to-orange-600 p-5 text-white dark:border-white/10">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-white/70">
                Suivi ceinture noire / Dans
              </p>

              <h2 className="mt-2 font-titre text-2xl font-bold">
                Modifier la validation des UV
              </h2>

              <p className="mt-2 text-sm font-medium text-white/70">
                Mets à jour les UV validées, la dominante et les points UV3.
              </p>
            </div>

            <button
              ref={closeButtonRef}
              type="button"
              onClick={onClose}
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white backdrop-blur-xl transition hover:bg-white/25 focus:outline-none focus:ring-4 focus:ring-white/30"
              aria-label="Fermer"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <form onSubmit={onSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 space-y-5 overflow-y-auto p-4 sm:p-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                  Dominante
                </span>

                <select
                  name="dominante"
                  value={form.dominante}
                  onChange={onChange}
                  className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 outline-none transition focus:border-orange-300 focus:ring-4 focus:ring-orange-100 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:focus:border-orange-300/50 dark:focus:ring-orange-500/10"
                >
                  <option value="">Sélectionner</option>
                  <option value="Technique">Technique</option>
                  <option value="Compétition">Compétition</option>
                </select>
              </label>

              <label className="block">
                <span className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                  Points UV3
                </span>

                <input
                  type="number"
                  name="uv3_points"
                  min="0"
                  value={form.uv3_points}
                  onChange={onChange}
                  className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 outline-none transition focus:border-orange-300 focus:ring-4 focus:ring-orange-100 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:focus:border-orange-300/50 dark:focus:ring-orange-500/10"
                />
              </label>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <CheckboxField
                name="uv1"
                label="UV1 validée"
                checked={form.uv1}
                onChange={onChange}
              />

              <CheckboxField
                name="uv2"
                label="UV2 validée"
                checked={form.uv2}
                onChange={onChange}
              />

              <CheckboxField
                name="uv3"
                label="UV3 validée"
                checked={form.uv3}
                onChange={onChange}
              />

              <CheckboxField
                name="uv4"
                label="UV4 validée"
                checked={form.uv4}
                onChange={onChange}
              />
            </div>

            <label className="block">
              <span className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                Observations
              </span>

              <textarea
                name="observations"
                value={form.observations}
                onChange={onChange}
                rows={5}
                placeholder="Notes, échéances, points à valider..."
                className="w-full resize-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium leading-7 text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-orange-300 focus:ring-4 focus:ring-orange-100 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:focus:border-orange-300/50 dark:focus:ring-orange-500/10"
              />
            </label>
          </div>

          <div className="sticky bottom-0 grid gap-2 border-t border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-900 sm:grid-cols-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:-translate-y-0.5 dark:border-white/10 dark:bg-white/[0.05] dark:text-white"
            >
              <FaTimes />
              Annuler
            </button>

            <button
              type="submit"
              disabled={saving}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-orange-600 px-5 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:bg-orange-700 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500"
            >
              {saving ? <FaSave className="animate-pulse" /> : <FaCheck />}
              {saving ? "Enregistrement..." : "Enregistrer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default DanGradeModal;